import { useState, useEffect, useCallback } from "react";

function GaugeBar({ percent, color, theme }) {
  const c = percent >= 90 ? "#ef4444" : percent >= 75 ? "#f59e0b" : color;
  return (
    <div style={{height:5,background:theme.border,borderRadius:3,overflow:"hidden",marginTop:5}}>
      <div style={{width:`${Math.min(percent,100)}%`,height:"100%",
        background:`linear-gradient(90deg,${c}88,${c})`,
        borderRadius:3,transition:"width .6s ease",boxShadow:`0 0 6px ${c}44`}}/>
    </div>
  );
}

function MetricCard({ icon, label, percent, line1, line2, color, theme }) {
  const c = percent >= 90 ? "#ef4444" : percent >= 75 ? "#f59e0b" : color;
  return (
    <div style={{background:theme.bgCard,border:`1px solid ${theme.border}`,
      borderRadius:8,padding:"10px 14px",
      boxShadow:theme===undefined?"none":"0 1px 3px rgba(0,0,0,0.1)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:13}}>{icon}</span>
          <span style={{fontSize:9,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
            letterSpacing:".08em",fontWeight:700}}>{label}</span>
        </div>
        <span style={{fontSize:20,fontFamily:"'Inter',sans-serif",fontWeight:800,color:c}}>
          {percent}<span style={{fontSize:11,fontWeight:600}}>%</span>
        </span>
      </div>
      <GaugeBar percent={percent} color={color} theme={theme}/>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
        <span style={{fontSize:10,color:theme.text,fontFamily:"'Inter',sans-serif",fontWeight:600}}>{line1}</span>
        <span style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>{line2}</span>
      </div>
    </div>
  );
}

function DiskCard({ mount, info, color, theme }) {
  const c = info.percent >= 90 ? "#ef4444" : info.percent >= 75 ? "#f59e0b" : color;
  return (
    <div style={{background:theme.bgCard,border:`1px solid ${theme.border}`,borderRadius:8,padding:"10px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:13}}>🖴</span>
          <span style={{fontSize:9,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
            letterSpacing:".08em",fontWeight:700}}>
            DISK <span style={{color:theme.textSub}}>{mount}</span>
          </span>
        </div>
        <span style={{fontSize:20,fontFamily:"'Inter',sans-serif",fontWeight:800,color:c}}>
          {info.percent}<span style={{fontSize:11,fontWeight:600}}>%</span>
        </span>
      </div>
      <GaugeBar percent={info.percent} color={color} theme={theme}/>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
        <span style={{fontSize:10,color:theme.text,fontFamily:"'Inter',sans-serif",fontWeight:600}}>
          {((info.used_gb ?? info.used/1073741824) || 0).toFixed(1)} GB / {((info.total_gb ?? info.total/1073741824) || 0).toFixed(1)} GB
        </span>
        <span style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
          {((info.free_gb ?? info.free/1073741824) || 0).toFixed(1)} GB free
        </span>
      </div>
    </div>
  );
}

export default function ServerMetrics({ apiUrl, accent, token, theme }) {
  const [metrics, setMetrics] = useState(null);
  const [error,   setError]   = useState(false);

  const fetch_ = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/metrics`, {
        signal: AbortSignal.timeout(8000),
        headers: { "X-Auth-Token": token },
      });
      const d = await r.json();
      setMetrics(d); setError(false);
    } catch { setError(true); }
  }, [apiUrl, token]);

  useEffect(() => {
    fetch_();
    const id = setInterval(fetch_, 15000);
    return () => clearInterval(id);
  }, [fetch_]);

  if (error || !metrics || metrics.error) return null;

  const gb    = mb => Math.round(mb / 1024 * 10) / 10;
  const allDisks = metrics.disk || metrics.disks || {};
const disks = Object.fromEntries(
  Object.entries(allDisks).filter(([k]) => k === "/" || k === "/mnt/apps" || k === "/data" || /^[A-Z]:$/.test(k))
);

  return (
    <div style={{borderBottom:`1px solid ${theme.border}`,
      padding:"12px 20px",background:theme.metricsBg}}>
      {/* server info bar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:accent,
            display:"block",boxShadow:`0 0 8px ${accent}`}}/>
          <span style={{fontFamily:"'Inter',sans-serif",fontSize:12,color:theme.text,fontWeight:700}}>
            {metrics.hostname}
          </span>
          <span style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
            background:theme.badge,border:`1px solid ${theme.border}`,
            padding:"1px 8px",borderRadius:4,fontWeight:600}}>
            {metrics.cpu?.cores} cores
          </span>
          <span style={{fontSize:11,color:theme.textSub,fontFamily:"'Inter',sans-serif",fontWeight:500}}>
            ⏱ {metrics.uptime}
          </span>
        </div>
        <div style={{display:"flex",gap:16,alignItems:"center"}}>
          <span style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
            Load avg: <span style={{color:theme.textSub,fontWeight:600}}>{metrics.load?.["1m"]}</span>
            {" / "}<span style={{color:theme.textMuted}}>{metrics.load?.["5m"]}</span>
            {" / "}<span style={{color:theme.textFaint||theme.textMuted}}>{metrics.load?.["15m"]}</span>
          </span>
          <span style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
            ↻ {new Date(metrics.timestamp).toLocaleTimeString()}
          </span>
        </div>
      </div>
      {/* cards */}
      <div style={{display:"grid",
        gridTemplateColumns:`repeat(${2+Object.keys(disks).length},1fr)`,gap:8}}>
        <MetricCard icon="⚡" label="CPU USAGE"
          percent={metrics.cpu?.percent}
          line1={`${metrics.cpu?.percent}% utilized`}
          line2={`${metrics.cpu?.cores} cores`}
          color="#06b6d4" theme={theme}/>
        <MetricCard icon="💾" label="RAM USAGE"
          percent={metrics.ram?.percent}
          line1={`${gb(metrics.ram?.used_mb)} GB / ${gb(metrics.ram?.total_mb)} GB`}
          line2={`${gb(metrics.ram?.free_mb)} GB free`}
          color="#8b5cf6" theme={theme}/>
        {Object.entries(disks).map(([mount,info])=>(
          <DiskCard key={mount} mount={mount} info={info} color="#10b981" theme={theme}/>
        ))}
      </div>
    </div>
  );
}
